import { useMemo } from "react";
import useSWR from "swr";
import { find, keyBy } from "lodash";
import * as aiccPromptAPI from "services/aiccPrompt";
import { AICC_SWR_CONFIG } from "./swrConfig";

/**
 * The model catalogue behind the dropdown and the Model Cost dialog (AICC2-328 AC-2).
 *
 * Global rather than per language, and read once: a prompt carries only a model id, and the name beside
 * it, the prices behind it and the reasoning efforts it accepts all come from here. `modelsById` is what
 * turns that id into something printable without every caller doing its own `find`.
 *
 * `defaultModel` is the backend's answer to "which model does an unconfigured prompt run on" — the
 * screen shows that rather than an empty select, and does not hardcode which model it is.
 */
export function useAiccPromptModels() {
  const { data, error, isLoading, mutate } = useSWR(
    ["aicc/llm/models"],
    () => aiccPromptAPI.fetchAiccPromptModels(),
    AICC_SWR_CONFIG
  );

  // Memoised, not derived inline: `modelsById` is a dependency of the column config, and a fresh array
  // every render would rebuild the columns every render.
  const models = useMemo(() => data ?? [], [data]);

  return {
    models,
    modelsById: useMemo(() => keyBy(models, "id"), [models]),
    defaultModel: useMemo(() => find(models, "isDefault"), [models]),
    isLoading,
    isError: !!error,
    mutate,
  };
}
